/** @jsxImportSource @emotion/react */
import Root from "pages/root";
import {
  ButtonContainer,
  Container,
  ContentContainer,
  HeaderContainer,
  TabContainer,
  TabItem,
  TableContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import PendingComplainList from "./pending-complain-list";
import ResolveComplainList from "./resolve-complain-list";
import {
  getCompletedComplain,
  getIncompletedComplain,
  resovleComplain,
  revokeComplain,
  TComplain,
} from "apis/meeting-room-complain";
import { useToast } from "hook/useToast";

const ComplainList = () => {
  const { showToast } = useToast();
  const [section, setSection] = useState<"미처리 민원" | "처리 완료 민원">("미처리 민원");
  const [checkedComplainList, setCheckedComplainList] = useState<TComplain[]>([]);

  const [totalPage, setTotalPage] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [complainList, setComplainList] = useState<TComplain[]>([]);

  const onChangeSection = (section: "미처리 민원" | "처리 완료 민원") => {
    setCheckedComplainList([]);
    setSection(section);
  };

  const onHandleCheck = (selectedComplain: TComplain) => {
    let updateComplainList = checkedComplainList;
    if (checkedComplainList.includes(selectedComplain)) {
      updateComplainList = updateComplainList.filter((item) => item !== selectedComplain);
    } else {
      updateComplainList = [...updateComplainList, selectedComplain];
    }
    setCheckedComplainList(updateComplainList);
  };

  const onHandleAllCheck = (isChecked: boolean, complainList: TComplain[]) => {
    if (isChecked) {
      setCheckedComplainList(complainList);
    } else {
      setCheckedComplainList([]);
    }
  };

  const getPendingComplainList = async (page: number) => {
    const res = await getIncompletedComplain(page);

    if (res.success) {
      setTotalPage(parseInt(res.data?.totalPageNumber.toString() ?? "1"));
      setPage(parseInt(res.data?.nowPageNumber?.toString() ?? "1"));

      setComplainList(res.data?.meetingRoomComplains ?? []);
    }
  };

  const getResolveComplainList = async (page: number) => {
    const res = await getCompletedComplain(page);

    if (res.success) {
      setTotalPage(parseInt(res.data?.totalPageNumber.toString() ?? "1"));
      setPage(parseInt(res.data?.nowPageNumber?.toString() ?? "1"));
      setComplainList(res.data?.meetingRoomComplains ?? []);
    }
  };

  const onHandleResolveComplain = async () => {
    if (checkedComplainList.length === 0) return;

    const meetingRoomComplainIdList = checkedComplainList.map((item) => item.id);
    const res = await resovleComplain(meetingRoomComplainIdList ?? []);
    if (res.success) {
      showToast(`${checkedComplainList.length}개의 민원을 완료 처리했습니다.`, "success");
      onChangePage(page);
    } else {
      showToast(res.message ?? "오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  const onHandleRevokeComplain = async () => {
    if (checkedComplainList.length === 0) return;

    const meetingRoomComplainIdList = checkedComplainList.map((item) => item.id);
    const res = await revokeComplain(meetingRoomComplainIdList ?? []);
    if (res.success) {
      showToast(`${checkedComplainList.length}개의 민원을 처리 취소했습니다.`, "success");
      onChangePage(page);
    } else {
      showToast(res.message ?? "오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  const onChangePage = async (page: number) => {
    setCheckedComplainList([]);

    if (section === "미처리 민원") getPendingComplainList(page);
    else if (section === "처리 완료 민원") getResolveComplainList(page);
  };

  useEffect(() => {
    if (section === "미처리 민원") getPendingComplainList(page);
    else if (section === "처리 완료 민원") getResolveComplainList(page);
  }, [section]);

  return (
    <Container>
      <HeaderContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{ padding: "24px 20px" }}
        >
          민원 내역 조회
        </UI.Typography>
        <TabContainer>
          <TabItem selected={section === "미처리 민원"} onClick={() => onChangeSection("미처리 민원")}>
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={
                section === "미처리 민원" ? "var(--alias-color-primary-normal" : "var(--alias-color-label-alternative)"
              }
            >
              미처리 민원
            </UI.Typography>
          </TabItem>
          <TabItem selected={section === "처리 완료 민원"} onClick={() => onChangeSection("처리 완료 민원")}>
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color={
                section === "처리 완료 민원"
                  ? "var(--alias-color-primary-normal"
                  : "var(--alias-color-label-alternative)"
              }
            >
              처리 완료 민원
            </UI.Typography>
          </TabItem>
        </TabContainer>
      </HeaderContainer>
      <ContentContainer>
        <ButtonContainer>
          {checkedComplainList.length > 0 ? (
            <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-primary-normal)">
              {checkedComplainList.length}개 선택
            </UI.Typography>
          ) : null}
          {checkedComplainList.length > 0 && (
            <UI.Button
              type="solid"
              size="medium"
              title={section === "미처리 민원" ? "완료 처리" : "처리 취소"}
              primary={false}
              onClick={section === "미처리 민원" ? onHandleResolveComplain : onHandleRevokeComplain}
            />
          )}
        </ButtonContainer>
        <PendingComplainList
          open={section === "미처리 민원"}
          complainList={complainList}
          checkedComplainList={checkedComplainList}
          totalPage={totalPage}
          nowPage={page}
          onChangePage={onChangePage}
          onHandleCheck={onHandleCheck}
          onHandleAllCheck={onHandleAllCheck}
        />
        <ResolveComplainList
          open={section === "처리 완료 민원"}
          complainList={complainList}
          checkedComplainList={checkedComplainList}
          totalPage={totalPage}
          nowPage={page}
          onChangePage={onChangePage}
          onHandleCheck={onHandleCheck}
          onHandleAllCheck={onHandleAllCheck}
        />
      </ContentContainer>
    </Container>
  );
};
export default ComplainList;
